import { addAlertThunk } from 'actions/alerts/AlertsActions';
import { setAuthToken } from 'actions/auth/AuthActions';
import {
    getCustomerByTokenThunk,
    lookupCustomerByPhoneThunk,
    lookupCustomerThunk,
    saveCustomerThunk
} from 'actions/customer/CustomerActions';
import { convertToCustomer } from 'actions/customer/CustomerConverter';
import { CustomerError, CustomerErrorType } from 'actions/customer/CustomerErrors';
import {
    INTAKE_FORM_ADD_PET,
    INTAKE_FORM_ERROR,
    INTAKE_FORM_SET_CUSTOMER,
    INTAKE_FORM_SET_LOADING,
    INTAKE_FORM_SET_PETS,
    INTAKE_FORM_SET_STEP,
    INTAKE_FORM_UPDATE_PET,
    IntakeFormAction
} from 'actions/intakeForm/IntakeFormActionsTypes';
import { fetchMarketplaceBySlugThunk } from 'actions/marketplace/MarketplaceActions';
import { fetchMasterDataThunk } from 'actions/masterData/MasterDataActions';
import {
    fetchPaymentMethodsThunk,
    resetPaymentMethods
} from 'actions/paymentMethods/PaymentMethodsActions';
import { push } from 'connected-react-router';
import { IntakeFormStep } from 'reducers/intakeForm/IntakeFormState';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import { AlertScreen } from 'types/Alert';
import { Customer, LeadSource, LeadStage } from 'types/Customer';
import { Pet, SavePet } from 'types/Pet';
import { hasValidCardOnFile } from 'utils/paymentMethodUtils';

const errorHandler = (error: unknown): IntakeFormAction => {
    console.error(error);

    return {
        type: INTAKE_FORM_ERROR
    };
};

const setLoading = (loading: boolean): IntakeFormAction => {
    return {
        type: INTAKE_FORM_SET_LOADING,
        payload: {
            loading
        }
    };
};

const setStep = (step: IntakeFormStep): IntakeFormAction => {
    return {
        type: INTAKE_FORM_SET_STEP,
        payload: { step }
    };
};

const setCustomer = (customer: Customer): IntakeFormAction => {
    return {
        type: INTAKE_FORM_SET_CUSTOMER,
        payload: { customer }
    };
};

const addPet = (pet: SavePet): IntakeFormAction => {
    return {
        type: INTAKE_FORM_ADD_PET,
        payload: { pet }
    };
};

const updatePet = (pet: SavePet): IntakeFormAction => {
    return {
        type: INTAKE_FORM_UPDATE_PET,
        payload: { pet }
    };
};

const setPets = (pets: Array<SavePet>): IntakeFormAction => {
    return {
        type: INTAKE_FORM_SET_PETS,
        payload: { pets }
    };
};

export const initIntakeFormThunk = (
    marketplaceSlug: string,
    customerToken?: string
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        try {
            dispatch(setLoading(true));

            const marketplace = await dispatch(fetchMarketplaceBySlugThunk(marketplaceSlug));

            await dispatch(fetchMasterDataThunk(marketplace.id));

            dispatch(resetPaymentMethods());

            if (customerToken) {
                try {
                    const customerDto = await dispatch(
                        getCustomerByTokenThunk(customerToken, marketplace.id)
                    );
                    const customer = convertToCustomer(customerDto);

                    dispatch(setCustomer(customer));

                    if (customer.pets.length > 0) {
                        dispatch(setPets(customer.pets));
                    }
                } catch (error) {
                    // Token lookup failed — fall through to empty form rather than blocking entry.
                    console.error(error);
                }
            }

            dispatch(setStep(IntakeFormStep.CustomerForm));
            dispatch(setLoading(false));
        } catch (error) {
            dispatch(errorHandler(error));

            // Redirect to 404 page
            dispatch(push('/'));
        }
    };
};

export const intakeFormBackThunk = (): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        const currentStep = getState().intakeForm.step;

        switch (currentStep) {
            case IntakeFormStep.AddPetForm:
                dispatch(setStep(IntakeFormStep.CustomerForm));
                break;
            case IntakeFormStep.PetList:
                dispatch(setStep(IntakeFormStep.AddPetForm));
                break;
            case IntakeFormStep.CardOnFile:
                dispatch(setStep(IntakeFormStep.PetList));
                break;
            default:
                break;
        }
    };
};

export const submitIntakeFormCustomerThunk = (
    customer: Customer
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch(setLoading(true));

            const {
                marketplace,
                intakeForm: { pets }
            } = getState();
            const marketplaceId = marketplace.data.id;

            const alreadyRegisteredMessage =
                'Oops! It looks like this email and/or phone number is already registered. Please reach out to us at your convenience so we can help.';

            const [emailResult, phoneResult] = await Promise.allSettled([
                dispatch(lookupCustomerThunk(customer.email, marketplaceId)),
                customer.phone
                    ? dispatch(lookupCustomerByPhoneThunk(customer.phone, marketplaceId))
                    : Promise.resolve(null)
            ]);

            const emailMatch = emailResult.status === 'fulfilled' ? emailResult.value : null;
            const phoneMatch = phoneResult.status === 'fulfilled' ? phoneResult.value : null;

            // Email and phone belong to different existing customers — cannot safely update either.
            if (emailMatch && phoneMatch && emailMatch.id !== phoneMatch.id) {
                dispatch(addAlertThunk(alreadyRegisteredMessage, AlertScreen.IntakeFormCustomer));

                return;
            }

            const lookupCustomer = emailMatch ?? phoneMatch;

            // No match found AND at least one lookup failed: we cannot tell whether a match
            // would have existed on the failed side, so we cannot safely create a new customer.
            if (
                !lookupCustomer &&
                (emailResult.status === 'rejected' || phoneResult.status === 'rejected')
            ) {
                dispatch(
                    addAlertThunk(
                        'Oops, something went wrong while checking your information. Please try again.',
                        AlertScreen.IntakeFormCustomer
                    )
                );

                return;
            }

            if (lookupCustomer && lookupCustomer.clientSince != null) {
                dispatch(addAlertThunk(alreadyRegisteredMessage, AlertScreen.IntakeFormCustomer));

                return;
            }

            const leadsEnabled = marketplace.data.settings.leadsEnabled;

            const enrichedCustomer: Customer = {
                ...customer,
                id: lookupCustomer?.id ?? customer.id,
                clientSince: lookupCustomer?.clientSince ?? null,
                conversionSource: null,
                ...(!lookupCustomer && {
                    leadSource: LeadSource.INTAKE_FORM,
                    ...(leadsEnabled && { leadStage: LeadStage.NEW })
                })
            };

            dispatch(setCustomer(enrichedCustomer));

            // When an existing lead is matched, preload their pets so the user
            // sees them on the PetList step instead of starting from scratch.
            const lookupPets = lookupCustomer?.pets ?? [];
            if (lookupPets.length > 0) {
                dispatch(setPets(lookupPets));
            }

            const hasPets = pets.length > 0 || lookupPets.length > 0;

            if (hasPets) {
                dispatch(setStep(IntakeFormStep.PetList));
            } else {
                dispatch(setStep(IntakeFormStep.AddPetForm));
            }
        } catch (error) {
            dispatch(errorHandler(error));
        } finally {
            dispatch(setLoading(false));
        }
    };
};

export const submitIntakeFormPetThunk = (
    pet: SavePet
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        try {
            dispatch(addPet(pet));

            dispatch(setStep(IntakeFormStep.PetList));
        } catch (error) {
            dispatch(errorHandler(error));
        }
    };
};

export const updateIntakeFormPetThunk = (
    pet: SavePet
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        try {
            dispatch(updatePet(pet));
        } catch (error) {
            dispatch(errorHandler(error));
        }
    };
};

export const submitIntakeFormThunk = (): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch(setLoading(true));

            const {
                intakeForm: { customer: intakeFormCustomer, pets },
                marketplace
            } = getState();

            const savedCustomer = await dispatch(
                saveCustomerThunk(
                    {
                        ...intakeFormCustomer,
                        pets: pets.map(pet => pet as Pet)
                    },
                    'intake-form'
                )
            );

            dispatch(setCustomer(savedCustomer));

            const requiresCardOnFile = marketplace.data.settings.intakeFormCardOnFileEnabled;

            if (!requiresCardOnFile) {
                dispatch(setStep(IntakeFormStep.Success));
                return;
            }

            if (!savedCustomer.id || !savedCustomer.token) {
                throw new CustomerError(CustomerErrorType.Unknown);
            }

            dispatch(resetPaymentMethods());
            dispatch(setAuthToken(savedCustomer.token));

            await dispatch(fetchPaymentMethodsThunk(savedCustomer.id));

            if (hasValidCardOnFile(getState().paymentMethods.paymentMethods)) {
                dispatch(setStep(IntakeFormStep.Success));
                return;
            }

            dispatch(setStep(IntakeFormStep.CardOnFile));
        } catch (error) {
            dispatch(errorHandler(error));

            dispatch(setStep(IntakeFormStep.CustomerForm));

            let errorMessage = 'Oops, something went wrong.';

            if (error instanceof CustomerError) {
                if (error.type === CustomerErrorType.PhoneAlreadyTaken) {
                    errorMessage =
                        'Oops! It looks like this email and/or phone number is already registered. Please reach out to us at your convenience so we can help.';
                }
            }

            dispatch(addAlertThunk(errorMessage, AlertScreen.IntakeFormCustomer));
        } finally {
            dispatch(setLoading(false));
        }
    };
};

export const setIntakeFormSmsMarketingOptInThunk = (
    smsMarketingOptIn: boolean
): ThunkAction<void, RootState, null, RootAction> => {
    return (dispatch, getState) => {
        const {
            intakeForm: { customer }
        } = getState();

        dispatch(setCustomer({ ...customer, smsMarketingOptIn }));
    };
};

export const completeIntakeFormCardOnFileThunk = (): ThunkAction<
    void,
    RootState,
    null,
    RootAction
> => {
    return dispatch => {
        dispatch(setStep(IntakeFormStep.Success));
    };
};
